import { PrimaryButton, SecondaryButton, TextButton } from '../components/Button';
import { Card, StatCard } from '../components/Card';
import { StatusChip } from '../components/Status';
import { Screen } from '../components/Screen';
import { copy } from '../copy';
import { showInstallPrompt } from '../lib/install';
import { navigationHref, openExternal } from '../lib/links';
import {
  formatClock,
  formatDayHeading,
  formatDrivingTime,
  formatKm,
  localDateOf,
} from '../lib/time';
import { allHandled, stopCounts } from '../state/route';
import { selectQueueCount, useAppStore } from '../state/store';

/** C1–C6 — Vandaag / dagkaart, inclusief leeg, skeleton, offline, onderweg en afgerond. */
export function DayScreen() {
  const route = useAppStore((s) => s.route);
  const routeState = useAppStore((s) => s.routeState);
  const online = useAppStore((s) => s.online);
  const fetchedAt = useAppStore((s) => s.fetchedAt);
  const queueCount = useAppStore(selectQueueCount);
  const navigate = useAppStore((s) => s.navigate);
  const openSheet = useAppStore((s) => s.openSheet);
  const refreshRoute = useAppStore((s) => s.refreshRoute);
  const installPromptAvailable = useAppStore((s) => s.installPromptAvailable);

  const heading = formatDayHeading(route?.route.routeDate ?? localDateOf());

  const header = (
    <div className="pad-x" style={{ paddingTop: 8 }}>
      <div className="row" style={{ gap: 4, justifyContent: 'flex-end', whiteSpace: 'nowrap' }}>
        <TextButton onClick={() => navigate('queue')}>{copy.day.queue}</TextButton>
        <TextButton onClick={() => navigate('settings')}>{copy.day.settings}</TextButton>
      </div>
      <h1 className="t-serif t-dagkop" style={{ marginTop: 4 }}>
        {heading}
      </h1>
      <p className="o-65" style={{ fontSize: 16, marginTop: 4, marginBottom: 0 }}>
        {copy.day.subtitle}
      </p>
    </div>
  );

  if (routeState === 'loading') {
    return (
      <Screen name="day">
        {header}
        <div className="grid-2 pad-x" style={{ paddingTop: 20 }} aria-hidden>
          {[0, 1, 2, 3].map((index) => (
            <div key={index} className="skeleton" style={{ height: 84 }} />
          ))}
        </div>
        <div className="stack pad-x" style={{ paddingTop: 14 }} aria-hidden>
          <div className="skeleton" style={{ height: 74 }} />
          <div className="skeleton" style={{ height: 62 }} />
        </div>
      </Screen>
    );
  }

  if (routeState === 'empty' || (!route && routeState !== 'error')) {
    return (
      <Screen name="day">
        {header}
        <p className="o-75 pad-x" style={{ fontSize: 20, paddingTop: 24 }}>
          {copy.day.empty}
        </p>
      </Screen>
    );
  }

  if (!route) {
    return (
      <Screen
        name="day"
        foot={
          <PrimaryButton onClick={() => void refreshRoute(true)}>{copy.day.retry}</PrimaryButton>
        }
      >
        {header}
        <p className="o-75 pad-x" style={{ fontSize: 20, paddingTop: 24 }}>
          {copy.day.loadError}
        </p>
      </Screen>
    );
  }

  const counts = stopCounts(route);
  const started = route.route.actualStartAt !== null;
  const pickupStarted = route.route.status === 'inProgress' || route.route.closedAt !== null;
  const completed = route.route.status === 'completed';

  const destination = {
    lat: route.pickupPoint.lat,
    lng: route.pickupPoint.lng,
    label: route.pickupPoint.name,
    addressLine: route.pickupPoint.addressLine,
    postalCodeCity: route.pickupPoint.postalCodeCity,
  };

  const foot = completed ? undefined : (
    <>
      <SecondaryButton onClick={() => openExternal(navigationHref(destination))}>
        {copy.day.navigateToPickup}
      </SecondaryButton>
      {pickupStarted ? (
        <PrimaryButton
          onClick={() =>
            navigate(started ? (allHandled(route) ? 'return' : 'ride') : 'load')
          }
        >
          {copy.day.continueRide}
        </PrimaryButton>
      ) : (
        <PrimaryButton onClick={() => openSheet('pickup')}>{copy.day.startPickup}</PrimaryButton>
      )}
    </>
  );

  return (
    <Screen name="day" foot={foot}>
      {header}

      {!online && fetchedAt ? (
        <div className="pad-x row" style={{ paddingTop: 8, gap: 8, flexWrap: 'wrap' }}>
          <span className="t-caption o-55">{copy.status.lastUpdated(formatClock(fetchedAt))}</span>
        </div>
      ) : null}

      {route.route.isProvisional && !completed ? (
        <p className="t-caption o-65 pad-x" style={{ paddingTop: 8, margin: 0 }}>
          {copy.day.provisional}
        </p>
      ) : null}

      {queueCount > 0 ? (
        <div className="pad-x" style={{ paddingTop: 10 }}>
          <StatusChip tone="waiting">{copy.status.queue(queueCount)}</StatusChip>
        </div>
      ) : null}

      {installPromptAvailable ? (
        <div className="pad-x" style={{ paddingTop: 12 }}>
          <Card>
            <div className="row row--between" style={{ gap: 12 }}>
              <span style={{ fontSize: 17, fontWeight: 600 }}>{copy.install.title}</span>
              <TextButton onClick={() => void showInstallPrompt()}>
                {copy.install.understood}
              </TextButton>
            </div>
          </Card>
        </div>
      ) : null}

      <div className="grid-2 pad-x" style={{ paddingTop: 20 }}>
        {started || completed ? (
          <>
            <StatCard value={String(counts.delivered)} label={copy.ret.delivered} tone="mint" />
            <StatCard
              value={String(counts.failed)}
              label={copy.ret.failed}
              tone={counts.failed > 0 ? 'terracotta' : 'default'}
            />
            <StatCard value={String(counts.open)} label={copy.day.stops} />
            <StatCard
              value={`± ${formatClock(route.route.plannedEndAt)}`}
              label={copy.day.expectedDone}
            />
          </>
        ) : (
          <>
            <StatCard value={String(route.route.stopCount)} label={copy.day.stops} />
            <StatCard value={formatKm(route.route.plannedDistanceMeters)} label={copy.day.planned} />
            <StatCard
              value={formatDrivingTime(route.route.plannedDurationSeconds)}
              label={copy.day.driving}
            />
            <StatCard
              value={`± ${formatClock(route.route.plannedEndAt)}`}
              label={copy.day.expectedDone}
            />
          </>
        )}
      </div>

      <div className="stack pad-x" style={{ paddingTop: 14 }}>
        <Card>
          <div className="row" style={{ gap: 14, alignItems: 'flex-start' }}>
            <span className="dot-pickup" aria-hidden />
            <div>
              <div style={{ fontSize: 17, fontWeight: 600 }}>
                {copy.day.pickupPoint(route.pickupPoint.name)}
              </div>
              <div className="o-75" style={{ fontSize: 16, marginTop: 2 }}>
                {route.pickupPoint.addressLine}, {route.pickupPoint.postalCodeCity.split(' ').slice(-1)[0]}
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="row row--between">
            <span style={{ fontSize: 17, fontWeight: 600 }}>
              {route.car ? route.car.displayName : copy.day.noCar}
            </span>
            {route.car ? <span className="plate">{route.car.licensePlate}</span> : null}
          </div>
        </Card>
      </div>
    </Screen>
  );
}
